<template>
  <section class="export-page" :dir="pageDirection">
    <div class="section-head">
      <h2>{{ tr("title") }}</h2>
      <p>{{ tr("description") }}</p>
    </div>

    <div class="export-grid">
      <article v-for="item in exportItems" :key="item.key" class="export-card">
        <div class="icon-box">
          <v-icon size="34">{{ item.icon }}</v-icon>
        </div>

        <div class="export-card-content">
          <h3>{{ item.title }}</h3>
          <p>{{ item.description }}</p>
        </div>

        <v-btn
          class="export-btn"
          block
          :loading="activeExport === item.key"
          :disabled="activeExport !== null && activeExport !== item.key"
          @click="item.action"
        >
          <span>{{ item.button }}</span>
          <v-icon class="download-icon">mdi-download</v-icon>
        </v-btn>
      </article>
    </div>

    <div class="export-note">
      <v-icon size="20">mdi-information-outline</v-icon>
      <span>{{ tr("note") }}</span>
    </div>
  </section>
</template>

<script setup>
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t, locale } = useI18n();

const activeExport = ref(null);

const tr = (key, params = {}) => t(`exportPage.${key}`, params);

const pageDirection = computed(() => (locale.value === "ar" ? "rtl" : "ltr"));

const isArabic = computed(() => locale.value === "ar");

const facilities = computed(() => [
  {
    id: 1,
    name: isArabic.value
      ? "مركز الشيخ عثمان الصحي"
      : "Sheikh Othman Health Center",
    type: isArabic.value ? "مركز صحي" : "Health Center",
    governorate: isArabic.value ? "عدن" : "Aden",
    district: isArabic.value ? "الشيخ عثمان" : "Sheikh Othman",
    fis: 48.4,
    priority: "P2",
  },
  {
    id: 2,
    name: isArabic.value
      ? "مركز كود العثماني الصحي"
      : "Kod Al-Othmani Health Center",
    type: isArabic.value ? "مركز رعاية أولية" : "Primary Health Care Center",
    governorate: isArabic.value ? "عدن" : "Aden",
    district: isArabic.value ? "الشيخ عثمان" : "Sheikh Othman",
    fis: 45.2,
    priority: "P2",
  },
]);

const ragData = computed(() => [
  {
    facility: isArabic.value
      ? "مركز الشيخ عثمان الصحي"
      : "Sheikh Othman Health Center",
    infrastructure: tr("ragValues.amber"),
    services: tr("ragValues.red"),
    workforce: tr("ragValues.amber"),
    finance: tr("ragValues.red"),
    governance: tr("ragValues.green"),
  },
  {
    facility: isArabic.value
      ? "مركز كود العثماني الصحي"
      : "Kod Al-Othmani Health Center",
    infrastructure: tr("ragValues.red"),
    services: tr("ragValues.amber"),
    workforce: tr("ragValues.amber"),
    finance: tr("ragValues.red"),
    governance: tr("ragValues.amber"),
  },
]);

const exportItems = computed(() => [
  {
    key: "facilities",
    icon: "mdi-file-delimited-outline",
    title: tr("cards.facilities.title"),
    description: tr("cards.facilities.description"),
    button: tr("buttons.csv"),
    action: exportFacilitiesCSV,
  },
  {
    key: "json",
    icon: "mdi-code-json",
    title: tr("cards.fullDataset.title"),
    description: tr("cards.fullDataset.description"),
    button: tr("buttons.json"),
    action: exportJSON,
  },
  {
    key: "rag",
    icon: "mdi-table-arrow-down",
    title: tr("cards.rag.title"),
    description: tr("cards.rag.description"),
    button: tr("buttons.csv"),
    action: exportRagCSV,
  },
]);

const downloadFile = (content, filename, type) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();

  URL.revokeObjectURL(url);
};

const escapeCSVValue = (value) =>
  `"${String(value ?? "").replace(/"/g, '""')}"`;

const toCSV = (rows) => {
  if (!Array.isArray(rows) || !rows.length) return "";

  const headers = Object.keys(rows[0]);

  const csvRows = [
    headers.map(escapeCSVValue).join(","),
    ...rows.map((row) =>
      headers.map((header) => escapeCSVValue(row[header])).join(",")
    ),
  ];

  return "\uFEFF" + csvRows.join("\n");
};

const runExport = async (key, callback) => {
  try {
    activeExport.value = key;

    await new Promise((resolve) => setTimeout(resolve, 250));
    callback();
  } catch (error) {
    console.error("Export Error:", error);
  } finally {
    activeExport.value = null;
  }
};

function exportFacilitiesCSV() {
  runExport("facilities", () => {
    const csv = toCSV(facilities.value);

    downloadFile(
      csv,
      isArabic.value ? "بيانات-المرافق.csv" : "facilities.csv",
      "text/csv;charset=utf-8;"
    );
  });
}

function exportRagCSV() {
  runExport("rag", () => {
    const csv = toCSV(ragData.value);

    downloadFile(
      csv,
      isArabic.value ? "مصفوفة-RAG.csv" : "rag-matrix.csv",
      "text/csv;charset=utf-8;"
    );
  });
}

function exportJSON() {
  runExport("json", () => {
    const data = {
      exported_at: new Date().toISOString(),
      language: locale.value,
      facilities: facilities.value,
      rag_matrix: ragData.value,
    };

    downloadFile(
      JSON.stringify(data, null, 2),
      isArabic.value ? "مجموعة-بيانات-DANA.json" : "dana-full-dataset.json",
      "application/json;charset=utf-8;"
    );
  });
}
</script>

<style scoped>
.export-page {
  min-height: 75vh;
  padding: 24px;
  overflow-x: hidden;
  background: #f4f6f9;
  color: #071126;
  font-family: "Inter", "Cairo", sans-serif;
}

.export-page[dir="rtl"] {
  font-family: "Cairo", "Inter", sans-serif;
}

.section-head {
  margin-bottom: 22px;
}

.export-page[dir="rtl"] .section-head {
  text-align: right;
}

.export-page[dir="ltr"] .section-head {
  text-align: left;
}

.section-head h2 {
  margin: 0;
  color: #15355c;
  font-size: 26px;
  font-weight: 900;
  line-height: 1.5;
}

.section-head p {
  max-width: 850px;
  margin: 8px 0 0;
  color: #64748b;
  font-size: 15px;
  line-height: 1.8;
}

.export-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
}

.export-card {
  display: flex;
  min-width: 0;
  min-height: 300px;
  flex-direction: column;
  padding: 24px;
  background: #fff;
  border: 1px solid #dde4ec;
  border-inline-start: 5px solid #15355c;
  border-radius: 18px;
  box-shadow: 0 8px 24px rgba(13, 34, 64, 0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.export-card:hover {
  transform: translateY(-3px);
  border-color: #b9c7d8;
  box-shadow: 0 14px 30px rgba(13, 34, 64, 0.11);
}

.icon-box {
  display: flex;
  width: 68px;
  height: 68px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
  color: #15355c;
  background: #eef5fb;
  border-radius: 18px;
}

.export-card-content {
  flex: 1;
  min-width: 0;
}

.export-card h3 {
  margin: 0 0 8px;
  color: #15355c;
  font-size: 19px;
  font-weight: 900;
  line-height: 1.6;
  overflow-wrap: break-word;
}

.export-card p {
  min-height: 52px;
  margin: 0;
  color: #64748b;
  font-size: 14px;
  line-height: 1.8;
}

.export-btn {
  min-height: 46px;
  margin-top: 16px;
  color: #fff !important;
  font-weight: 900;
  text-transform: none;
  background: linear-gradient(135deg, #15355c, #0d2240) !important;
  border-radius: 12px;
}

.download-icon {
  margin-inline-start: 8px;
}

.export-note {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin-top: 18px;
  padding: 13px 16px;
  color: #52657d;
  font-size: 13px;
  line-height: 1.8;
  background: #fff;
  border: 1px solid #dbe3ee;
  border-radius: 12px;
}

.export-note :deep(.v-icon) {
  flex-shrink: 0;
  margin-top: 2px;
  color: #15355c;
}

@media (max-width: 1100px) {
  .export-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .export-card:last-child {
    grid-column: 1 / -1;
  }
}

@media (max-width: 700px) {
  .export-page {
    min-height: 100vh;
    padding: 16px 10px 28px;
  }

  .section-head {
    margin-bottom: 17px;
  }

  .section-head h2 {
    font-size: 22px;
  }

  .section-head p {
    font-size: 13px;
    line-height: 1.75;
  }

  .export-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .export-card,
  .export-card:last-child {
    min-height: auto;
    grid-column: auto;
    padding: 15px;
    border-radius: 16px;
  }

  .export-card:hover {
    transform: none;
  }

  .icon-box {
    width: 54px;
    height: 54px;
    margin-bottom: 13px;
    border-radius: 14px;
  }

  .icon-box :deep(.v-icon) {
    font-size: 29px !important;
  }

  .export-card h3 {
    font-size: 16px;
  }

  .export-card p {
    min-height: auto;
    font-size: 12px;
    line-height: 1.7;
  }

  .export-btn {
    width: 100%;
    min-height: 48px;
    margin-top: 14px;
  }

  .export-note {
    padding: 12px;
    font-size: 12px;
    border-radius: 12px;
  }
}

@media (max-width: 380px) {
  .export-page {
    padding-inline: 8px;
  }

  .export-card {
    padding: 13px;
  }
}
</style>
